import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Books, Star, Check, VideoCamera, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export const PackageCard = ({ pkg, index = 0, featured = false }) => {
  const { addItem, inCart } = useCart();
  const owned = inCart(pkg.id);
  const isTutor = pkg.kind === "tutoring";
  const isBundle = pkg.kind === "bundle";

  if (isBundle) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.07, type: "spring", stiffness: 90 }}
        className="group relative bg-white sticker rounded-3xl overflow-hidden flex flex-col ring-2 ring-brand-grape/40"
        data-testid={`package-card-${pkg.slug}`}
      >
        <div className="h-44 overflow-hidden relative" style={{ backgroundColor: `${pkg.color}22` }}>
          {pkg.image && <img src={pkg.image} alt={pkg.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
          <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white tracking-[0.12em] uppercase bg-brand-grape">
            Bundle · Save {pkg.savings_pct}%
          </span>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-heading font-bold text-xl text-ink tracking-tight">{pkg.title}</h3>
          <p className="font-body text-ink-soft text-sm mt-1 leading-relaxed flex-1">{pkg.tagline}</p>
          <div className="flex items-center gap-2 mt-3 text-ink-soft text-sm font-semibold">
            <Books size={18} weight="duotone" style={{ color: pkg.color }} />
            {(pkg.includes?.length ?? 0)} packages · {pkg.lesson_count} lessons
          </div>
          <div className="flex items-center justify-between mt-5">
            <span className="font-heading font-bold text-2xl text-ink flex items-baseline gap-2">
              ${Number(pkg.price).toFixed(0)}
              {pkg.original_price && <span className="font-body text-base font-semibold text-ink-soft line-through">${Number(pkg.original_price).toFixed(0)}</span>}
            </span>
            {owned ? (
              <Button disabled className="rounded-full bg-brand-grass/15 text-brand-grass font-semibold" data-testid={`added-${pkg.slug}`}>
                <Check size={18} weight="bold" className="mr-1" /> In cart
              </Button>
            ) : (
              <Button
                onClick={() => { addItem(pkg); toast.success(`${pkg.title} added to cart!`); }}
                className="rounded-full bg-brand-grape hover:bg-brand-grape/90 text-white font-semibold transition-transform hover:-translate-y-0.5"
                data-testid={`add-to-cart-${pkg.slug}`}
              >
                Add bundle
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07, type: "spring", stiffness: 90 }}
      className={`group bg-white sticker rounded-3xl overflow-hidden flex flex-col ${featured ? "sm:col-span-2" : ""}`}
      data-testid={`package-card-${pkg.slug}`}
    >
      <Link to={`/catalog/${pkg.slug}`} className="block relative overflow-hidden">
        <div className={`${featured ? "h-56" : "h-44"} overflow-hidden`} style={{ backgroundColor: `${pkg.color}22` }}>
          {pkg.image ? (
            <img src={pkg.image} alt={pkg.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="w-full h-full grid place-items-center">
              <Books size={48} weight="duotone" style={{ color: pkg.color }} />
            </div>
          )}
        </div>
        <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white tracking-[0.15em] uppercase" style={{ backgroundColor: pkg.color }}>
          {pkg.level}
        </span>
        {pkg.featured && (
          <span className="absolute top-3 right-3 grid place-items-center w-8 h-8 rounded-full bg-brand-sun text-ink">
            <Star size={16} weight="fill" />
          </span>
        )}
      </Link>

      <div className="p-6 flex flex-col flex-1">
        <Link to={`/catalog/${pkg.slug}`}>
          <h3 className="font-heading font-bold text-xl text-ink tracking-tight hover:text-brand-sky transition-colors">{pkg.title}</h3>
        </Link>
        <p className="font-body text-ink-soft text-sm mt-1 leading-relaxed flex-1">{pkg.tagline}</p>
        <div className="flex items-center gap-2 mt-3 text-ink-soft text-sm font-semibold">
          {isTutor ? (
            <><Clock size={18} weight="duotone" style={{ color: pkg.color }} /> {pkg.duration} · {pkg.format}</>
          ) : (
            <><Books size={18} weight="duotone" style={{ color: pkg.color }} /> {pkg.lesson_count} lessons</>
          )}
        </div>
        <div className="flex items-center justify-between mt-5">
          <span className="font-heading font-bold text-2xl text-ink">
            {isTutor && <span className="block font-body text-xs font-semibold text-ink-soft tracking-wide">Starting at</span>}
            ${Number(pkg.price).toFixed(0)}{isTutor && <span className="text-base font-semibold text-ink-soft">/hr</span>}
          </span>
          {isTutor ? (
            <Link to={`/catalog/${pkg.slug}`} data-testid={`book-${pkg.slug}`}>
              <Button className="rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold transition-transform hover:-translate-y-0.5">
                Book a session
              </Button>
            </Link>
          ) : owned ? (
            <Button disabled className="rounded-full bg-brand-grass/15 text-brand-grass font-semibold" data-testid={`added-${pkg.slug}`}>
              <Check size={18} weight="bold" className="mr-1" /> In cart
            </Button>
          ) : (
            <Button
              onClick={() => { addItem(pkg); toast.success(`${pkg.title} added to cart!`); }}
              className="rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold transition-transform hover:-translate-y-0.5"
              data-testid={`add-to-cart-${pkg.slug}`}
            >
              Add to cart
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};
